import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useMemo, useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  UsersThree, Funnel, GearSix, Gauge, SignOut, ChatsCircle, List, X, PlugsConnected, Wrench,
  Database, Package, CaretDown, CaretRight
} from "@phosphor-icons/react";

const NAV = [
  { to: "/", label: "Home", icon: Gauge, roles: ["admin", "manager", "salesperson"] },
  { to: "/leads", label: "Leads", icon: Funnel, roles: ["admin", "manager", "salesperson"] },
  { to: "/service-crm", label: "Service", icon: Wrench, roles: ["admin", "manager", "salesperson"] },
  { to: "/users", label: "Team", icon: UsersThree, roles: ["admin", "manager"] },
  { to: "/integrations", label: "Sources", icon: PlugsConnected, roles: ["admin", "manager"] },
  {
    label: "Master",
    icon: Database,
    roles: ["admin"],
    children: [
      { to: "/products", label: "Product Master", icon: Package, roles: ["admin"] },
      { to: "/templates", label: "Template Master", icon: ChatsCircle, roles: ["admin"] }
    ]
  },
  { to: "/settings", label: "Settings", icon: GearSix, roles: ["admin", "manager"] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const visibleNav = useMemo(() => {
    if (!user) return [];

    const dept = (user.department || "all").toLowerCase();

    return NAV.filter((n) => {
      // Check role authorization
      if (n.roles && !n.roles.includes(user.role)) return false;

      // Department scope checks for salesperson
      if (user.role === "salesperson") {
        if (n.to === "/leads" && dept === "service") return false;
        if (n.to === "/service-crm" && dept === "lead") return false;
      }

      // Check children if any
      if (n.children) {
        const visibleChildren = n.children.filter((c) => !c.roles || c.roles.includes(user.role));
        if (visibleChildren.length === 0) return false;
      }

      return true;
    }).map((n) => {
      if (n.children) {
        return {
          ...n,
          children: n.children.filter((c) => !c.roles || c.roles.includes(user.role)),
        };
      }
      return n;
    });
  }, [user]);

  // Track route transitions to distinguish returning from lead detail vs other menus
  useEffect(() => {
    const current = sessionStorage.getItem("leadpilot_current_route") || "";
    if (current && current !== location.pathname) {
      sessionStorage.setItem("leadpilot_prev_route", current);
    }
    sessionStorage.setItem("leadpilot_current_route", location.pathname);
  }, [location.pathname]);

  // close drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  if (!user) return null;

  return (
    <div className="h-screen bg-white flex flex-col md:flex-row overflow-hidden" data-testid="app-shell">
      {/* Mobile top bar */}
      <header className="md:hidden sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-[#E5E7EB] flex items-center justify-between px-4 h-14 shrink-0"
        style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="font-heading font-black text-lg tracking-tighter">
          LEAD<span className="text-[#002FA7]">/</span>PILOT
        </div>
        <button onClick={() => setDrawerOpen(true)} data-testid="mobile-menu-btn" className="p-2 -mr-2">
          <List size={22} weight="bold" />
        </button>
      </header>

      {/* Desktop sidebar - sticky & fixed height */}
      <aside className="hidden md:flex w-64 bg-[#F9FAFB] border-r border-[#E5E7EB] flex-col shrink-0 h-screen sticky top-0 overflow-hidden" data-testid="sidebar">
        <Brand />
        <Nav items={visibleNav} />
        <UserCard user={user} onLogout={logout} />
      </aside>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDrawerOpen(false)} />
          <aside className="relative w-72 max-w-[85%] bg-white border-r border-[#E5E7EB] flex flex-col h-full" style={{ paddingTop: "env(safe-area-inset-top)" }}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E7EB] shrink-0">
              <Brand inline />
              <button onClick={() => setDrawerOpen(false)} data-testid="mobile-drawer-close" className="p-2 -mr-2"><X size={20} weight="bold" /></button>
            </div>
            <Nav items={visibleNav} onItemClick={() => setDrawerOpen(false)} />
            <UserCard user={user} onLogout={logout} />
          </aside>
        </div>
      )}

      {/* Main - independently scrollable */}
      <main className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden h-full pb-20 md:pb-0">
        <div className="px-4 sm:px-6 md:px-8 py-6 md:py-8 max-w-screen-2xl mx-auto">
          {children}
        </div>
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-[#E5E7EB] grid"
        style={{ gridTemplateColumns: `repeat(${visibleNav.filter(n => n.to).length}, minmax(0, 1fr))`, paddingBottom: "env(safe-area-inset-bottom)" }}
        data-testid="bottom-nav">
        {visibleNav.filter(n => n.to).map(({ to, label, icon: Icon }) => (
          <NavLink key={to} to={to} end={to === "/"} data-testid={`bnav-${label.toLowerCase()}`}
            state={to === "/leads" ? { fromMenuNav: true } : undefined}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-bold uppercase tracking-wider transition-colors ${isActive ? "text-[#002FA7]" : "text-[#6B7280]"
              }`
            }>
            <Icon size={20} weight="bold" />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

function Brand({ inline }) {
  return (
    <div className={inline ? "shrink-0" : "px-6 py-6 border-b border-[#E5E7EB] shrink-0"}>
      <div className="font-heading font-black text-2xl tracking-tighter text-[#111827]">
        LEAD<span className="text-[#002FA7]">/</span>PILOT
      </div>
      {!inline && (
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#6B7280] mt-1">B2B Lead OS</div>
      )}
    </div>
  );
}

function Nav({ items, onItemClick }) {
  const location = useLocation();
  const [openSubmenus, setOpenSubmenus] = useState({
    Master: true,
  });

  const toggleSubmenu = (label) => {
    setOpenSubmenus((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  return (
    <nav className="flex-1 py-4 overflow-y-auto">
      {items.map((item) => {
        if (item.children) {
          const isOpen = openSubmenus[item.label] ?? true;
          const isAnyChildActive = item.children.some(
            (c) => location.pathname === c.to || location.pathname.startsWith(c.to + "/")
          );
          const Icon = item.icon;

          return (
            <div key={item.label} className="my-0.5">
              <button
                type="button"
                onClick={() => toggleSubmenu(item.label)}
                data-testid={`nav-menu-${item.label.toLowerCase()}`}
                className={`w-full flex items-center justify-between px-6 py-3 text-sm font-bold tracking-tight transition-colors border-l-2 cursor-pointer ${isAnyChildActive
                    ? "border-[#002FA7] text-[#002FA7] bg-[#EEF2FF]/40"
                    : "border-transparent text-[#374151] hover:bg-[#F3F4F6]"
                  }`}
              >
                <div className="flex items-center gap-3">
                  <Icon size={18} weight="bold" />
                  <span>{item.label}</span>
                </div>
                {isOpen ? (
                  <CaretDown size={14} weight="bold" className="text-[#6B7280]" />
                ) : (
                  <CaretRight size={14} weight="bold" className="text-[#6B7280]" />
                )}
              </button>

              {isOpen && (
                <div className="bg-[#F3F4F6]/60 border-y border-[#E5E7EB]/50 py-1 space-y-0.5">
                  {item.children.map(({ to, label, icon: ChildIcon }) => (
                    <NavLink
                      key={to}
                      to={to}
                      end={to === "/"}
                      onClick={onItemClick}
                      data-testid={`nav-sub-${label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={({ isActive }) =>
                        `flex items-center gap-2.5 pl-12 pr-6 py-2 text-xs font-bold tracking-tight transition-colors ${isActive
                          ? "text-[#002FA7] bg-white font-extrabold border-l-2 border-[#002FA7]"
                          : "text-[#4B5563] hover:text-[#111827] hover:bg-white/60 border-l-2 border-transparent"
                        }`
                      }
                    >
                      <ChildIcon size={15} weight="bold" />
                      <span>{label}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        }

        const { to, label, icon: Icon } = item;
        return (
          <NavLink
            key={to}
            to={to}
            end={to === "/"}
            state={to === "/leads" ? { fromMenuNav: true } : undefined}
            onClick={onItemClick}
            data-testid={`nav-${label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-6 py-3 text-sm font-bold tracking-tight transition-colors border-l-2 ${isActive ? "border-[#002FA7] bg-white text-[#002FA7]" : "border-transparent text-[#374151] hover:bg-[#F3F4F6]"
              }`
            }
          >
            <Icon size={18} weight="bold" />
            <span>{label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}

function UserCard({ user, onLogout }) {
  return (
    <div className="px-6 py-4 border-t border-[#E5E7EB] shrink-0 mt-auto bg-[#F9FAFB]">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 bg-[#002FA7] text-white font-bold flex items-center justify-center shrink-0" data-testid="user-avatar">
          {user.name?.[0]?.toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold truncate" data-testid="user-name">{user.name}</div>
          <div className="text-[10px] uppercase tracking-widest text-[#6B7280] font-bold">{user.role}</div>
        </div>
        <button onClick={onLogout} data-testid="logout-button" className="p-2 hover:bg-[#F3F4F6] transition-colors shrink-0" title="Logout">
          <SignOut size={18} weight="bold" />
        </button>
      </div>
    </div>
  );
}
